import React, { useEffect, useMemo, useState } from "react";
import interiorCatalogApi from "../../services/interiorCatalogApi";
import ItemAssemblyPreview3D from "./ItemAssemblyPreview3D";

function prettyJson(value) {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return "{}";
  }
}

function parseJsonSafe(value, fallback = {}) {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function defaultMetadata() {
  return {
    enabled: true,
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    parameters: {},
  };
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function getComponentAsset(component, assets = []) {
  if (component?.asset?.id) return component.asset;

  return (
    assets.find((asset) => String(asset.id) === String(component.asset_id)) ||
    null
  );
}

function normalizeComponents(components = [], assets = []) {
  return components.map((component) => ({
    ...component,
    asset: getComponentAsset(component, assets),
    metadata_json: {
      ...defaultMetadata(),
      ...(component.metadata_json || {}),
      position: {
        ...defaultMetadata().position,
        ...(component.metadata_json?.position || {}),
      },
      rotation: {
        ...defaultMetadata().rotation,
        ...(component.metadata_json?.rotation || {}),
      },
      parameters: component.metadata_json?.parameters || {},
    },
  }));
}

export default function ItemComponentsEditor({
  item,
  itemMounting,
  itemMountingItems = [],
  setItem,
  onSaveItemPatch,
  components = [],
  setComponents,
  assets = [],
  componentItems = [],
  assetSearch,
  setAssetSearch,
  componentCategories = [],
  parameterDefinitions = [],
  fullScreen = false,
  onReload,
}) {
  const normalizedComponents = useMemo(
    () => normalizeComponents(components, assets),
    [components, assets]
  );

  const [selectedComponentId, setSelectedComponentId] = useState(null);
  const [componentForm, setComponentForm] = useState({
    id: null,
    asset_id: "",
    asset_role: "component.generic",
    component_item_id: "",
    quantity: 1,
    is_primary: 0,
    sort_order: 0,
    metadata_json: defaultMetadata(),
  });
  const [showDrillings, setShowDrillings] = useState(false);
  const [showOrigins, setShowOrigins] = useState(true);
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
const [assetModalSearch, setAssetModalSearch] = useState("");
const [assetModalResults, setAssetModalResults] = useState([]);
const [selectedAssetId, setSelectedAssetId] = useState("");
const [addAssetRole, setAddAssetRole] = useState("component.generic");
const [addingAsset, setAddingAsset] = useState(false);

  const [metadataText, setMetadataText] = useState(
    prettyJson(defaultMetadata())
  );

  const selectedComponent = useMemo(() => {
    return normalizedComponents.find(
      (component) => String(component.id) === String(selectedComponentId)
    );
  }, [normalizedComponents, selectedComponentId]);

  const itemAssemblyParameters = useMemo(() => {
  return item?.attributes_json?.assembly_parameters || {};
}, [item]);

function updateItemAssemblyParameter(key, value) {
  if (!key) return;

  setItem?.((prev) => ({
    ...(prev || {}),
    attributes_json: {
      ...(prev?.attributes_json || {}),
      assembly_parameters: {
        ...(prev?.attributes_json?.assembly_parameters || {}),
        [key]: value,
      },
    },
  }));
}

function removeItemAssemblyParameter(key) {
  if (!key) return;

  setItem?.((prev) => {
    const nextAssemblyParams = {
      ...(prev?.attributes_json?.assembly_parameters || {}),
    };

    delete nextAssemblyParams[key];

    return {
      ...(prev || {}),
      attributes_json: {
        ...(prev?.attributes_json || {}),
        assembly_parameters: nextAssemblyParams,
      },
    };
  });
}

async function saveItemAssemblyParameters() {
  if (!item?.id) return;

  await onSaveItemPatch?.({
    attributes_json: {
      ...(item.attributes_json || {}),
      assembly_parameters: itemAssemblyParameters,
    },
  });
}

  function editComponent(component) {
    const meta = {
      ...defaultMetadata(),
      ...(component.metadata_json || {}),
      position: {
        ...defaultMetadata().position,
        ...(component.metadata_json?.position || {}),
      },
      rotation: {
        ...defaultMetadata().rotation,
        ...(component.metadata_json?.rotation || {}),
      },
      parameters: component.metadata_json?.parameters || {},
    };

    setSelectedComponentId(component.id);

    setComponentForm({
  id: component.id,
  asset_id: component.asset_id || "",
  asset_role: component.asset_role || "component.generic",
  component_item_id: component.component_item_id || component.component_item?.id || "",
  quantity: component.quantity ?? 1,
  is_primary: component.is_primary ? 1 : 0,
  sort_order: component.sort_order || 0,
  metadata_json: meta,
});

    setMetadataText(prettyJson(meta));
  }

  function newComponent() {
    const meta = defaultMetadata();

    setSelectedComponentId(null);
    setComponentForm({
  id: null,
  asset_id: "",
  asset_role: "component.generic",
  component_item_id: "",
  quantity: 1,
  is_primary: 0,
  sort_order: normalizedComponents.length,
  metadata_json: meta,
});
    setMetadataText(prettyJson(meta));
  }

  function updateMeta(path, value) {
    const next = {
      ...componentForm.metadata_json,
      position: {
        ...(componentForm.metadata_json?.position || {}),
      },
      rotation: {
        ...(componentForm.metadata_json?.rotation || {}),
      },
      parameters: {
        ...(componentForm.metadata_json?.parameters || {}),
      },
    };

    if (path[0] === "position") {
      next.position[path[1]] = value;
    }

    if (path[0] === "rotation") {
      next.rotation[path[1]] = value;
    }

    if (path[0] === "enabled") {
      next.enabled = Boolean(value);
    }

    setComponentForm((prev) => ({
      ...prev,
      metadata_json: next,
    }));

    setMetadataText(prettyJson(next));
  }

  function updateParameter(key, value) {
  if (!key) return;

  const next = {
    ...componentForm.metadata_json,
    position: {
      ...(componentForm.metadata_json?.position || {}),
    },
    rotation: {
      ...(componentForm.metadata_json?.rotation || {}),
    },
    parameters: {
      ...(componentForm.metadata_json?.parameters || {}),
      [key]: value,
    },
  };

  setComponentForm((prev) => ({
    ...prev,
    metadata_json: next,
  }));

  setMetadataText(prettyJson(next));
}

function removeParameter(key) {
  const nextParameters = {
    ...(componentForm.metadata_json?.parameters || {}),
  };

  delete nextParameters[key];

  const next = {
    ...componentForm.metadata_json,
    parameters: nextParameters,
  };

  setComponentForm((prev) => ({
    ...prev,
    metadata_json: next,
  }));

  setMetadataText(prettyJson(next));
}

function addParameter(paramKey) {
  if (!paramKey) return;

  const params = componentForm.metadata_json?.parameters || {};

  if (params[paramKey] !== undefined) return;

  updateParameter(paramKey, "");
}

  async function reloadComponents() {
  if (!item?.id) return;

  if (onReload) {
    await onReload();
    return;
  }

  const res = await interiorCatalogApi.listItemAssets(item.id);
  setComponents?.(res.items || []);
}

async function searchAssetModalItems(searchText = "") {
  try {
    const res = await interiorCatalogApi.listAssets({
      search: String(searchText || "").trim(),
      limit: 100,
      offset: 0,
    });

    setAssetModalResults(Array.isArray(res) ? res : res?.items || []);
  } catch (err) {
    console.error("search asset modal error:", err);
    setAssetModalResults([]);
  }
}

useEffect(() => {
  if (!showAddAssetModal) return;

  const t = setTimeout(() => {
    searchAssetModalItems(assetModalSearch);
  }, 300);

  return () => clearTimeout(t);
}, [showAddAssetModal, assetModalSearch]);

async function addSelectedAssetToItem() {
  if (!item?.id || !selectedAssetId) return;

  try {
    setAddingAsset(true);

    await interiorCatalogApi.addAssetAsItemComponent(item.id, {
      asset_id: Number(selectedAssetId),
      asset_role: addAssetRole || "component.generic",
      quantity: 1,
      is_primary: normalizedComponents.length === 0 ? 1 : 0,
      sort_order: normalizedComponents.length,
      metadata_json: defaultMetadata(),
    });

    setShowAddAssetModal(false);
    setAssetModalSearch("");
    setAssetModalResults([]);
    setSelectedAssetId("");
    setAddAssetRole("component.generic");

    await reloadComponents();
  } catch (err) {
    console.error("add selected asset to item error:", err);
    alert(
      err?.response?.data?.message ||
        err?.message ||
        "Failed to add asset to item."
    );
  } finally {
    setAddingAsset(false);
  }
}

  async function saveComponent() {
    const selectedComponentItem = componentItems.find(
  (x) => String(x.id) === String(componentForm.component_item_id)
);
    if (!item?.id || (!componentForm.component_item_id && !componentForm.asset_id)) return;

    const metadata = parseJsonSafe(metadataText, componentForm.metadata_json);

    const payload = {
  component_item_id: componentForm.component_item_id
  ? Number(componentForm.component_item_id)
  : null,
  asset_id: Number(selectedComponentItem?.asset_id || componentForm.asset_id),
  asset_role: componentForm.asset_role || "component.generic",
  quantity: Number(componentForm.quantity || 1),
  is_primary: componentForm.is_primary ? 1 : 0,
  sort_order: Number(componentForm.sort_order || 0),
  metadata_json: metadata,
};

    if (componentForm.id) {
      await interiorCatalogApi.updateItemAsset(componentForm.id, payload);
    } else {
      await interiorCatalogApi.createItemAsset(item.id, payload);
    }

    await reloadComponents();
  }

  async function deleteComponent() {
    if (!componentForm.id) return;
    if (!window.confirm("Delete component?")) return;

    await interiorCatalogApi.deleteItemAsset(componentForm.id);
    await reloadComponents();
    newComponent();
  }

  function renderItemInfo() {
  return (
    <div className="border rounded p-3 bg-white space-y-3 mb-3">
      <div>
        <div className="font-semibold text-sm">Item info</div>
        <div className="text-xs text-slate-500">
          Commercial item data used by this assembly.
        </div>
      </div>

      <Field label="Name">
        <input
          value={item?.name || ""}
          onChange={(e) =>
            setItem?.((prev) => ({
              ...(prev || {}),
              name: e.target.value,
            }))
          }
          className="w-full px-3 py-2 border rounded"
        />
      </Field>

      <Field label="Code">
        <input
          value={item?.code || ""}
          onChange={(e) =>
            setItem?.((prev) => ({
              ...(prev || {}),
              code: e.target.value,
            }))
          }
          className="w-full px-3 py-2 border rounded"
        />
      </Field>

      <Field label="SKU">
        <input
          value={item?.sku || ""}
          onChange={(e) =>
            setItem?.((prev) => ({
              ...(prev || {}),
              sku: e.target.value,
            }))
          }
          className="w-full px-3 py-2 border rounded"
        />
      </Field>

      <button
        type="button"
        onClick={() =>
          onSaveItemPatch?.({
            name: item?.name || "",
            code: item?.code || "",
            sku: item?.sku || "",
          })
        }
        className="w-full px-3 py-2 bg-green-600 text-white rounded text-sm"
      >
        Save item info
      </button>

      <div className="border-t pt-3 space-y-3">
  <div>
    <div className="font-semibold text-sm">Assembly parameters</div>
    <div className="text-xs text-slate-500">
      Parameters used by this orderable item assembly.
    </div>
  </div>

  <Field label="Add assembly parameter">
    <select
      value=""
      onChange={(e) => {
        const key = e.target.value;
        if (!key) return;

        const def = parameterDefinitions.find(
          (p) => String(p.param_key) === String(key)
        );

        updateItemAssemblyParameter(
          key,
          def?.default_value ?? ""
        );
      }}
      className="w-full px-3 py-2 border rounded bg-white"
    >
      <option value="">Select parameter...</option>

      {parameterDefinitions
        .filter(
          (param) =>
            itemAssemblyParameters[param.param_key] === undefined
        )
        .map((param) => (
          <option key={param.id || param.param_key} value={param.param_key}>
            {param.label || param.param_key}
          </option>
        ))}
    </select>
  </Field>

  {Object.entries(itemAssemblyParameters).length ? (
    <div className="space-y-2">
      {Object.entries(itemAssemblyParameters).map(([key, value]) => {
        const def = parameterDefinitions.find(
          (p) => String(p.param_key) === String(key)
        );

        return (
          <div
            key={key}
            className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center"
          >
            <div className="px-2 py-1 border rounded bg-white text-sm">
              {def?.label || key}
              <div className="text-[10px] text-slate-400">{key}</div>

              <input
              value={value}
              onChange={(e) =>
                updateItemAssemblyParameter(key, e.target.value)
              }
              className="px-2 py-1 border rounded text-sm"
              placeholder="value / expression"
            />

            <button
              type="button"
              onClick={() => removeItemAssemblyParameter(key)}
              className="px-2 py-1 border rounded text-red-600 text-xs"
            >
              ×
            </button>
            </div>

            
          </div>
        );
      })}
    </div>
  ) : (
    <div className="text-xs text-slate-400">
      No assembly parameters selected.
    </div>
  )}

  <button
    type="button"
    onClick={saveItemAssemblyParameters}
    className="w-full px-3 py-2 bg-blue-600 text-white rounded text-sm"
  >
    Save assembly parameters
  </button>
</div>
    </div>
  );
}

 function renderComponentsList() {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-sm">Components</div>
          <div className="text-xs text-slate-500">
            Items inside this item assembly.
          </div>
        </div>

        <button
  type="button"
  disabled={!item?.id}
  onClick={() => setShowAddAssetModal(true)}
  className="px-3 py-1.5 border rounded bg-white text-sm disabled:opacity-40"
>
  + Add Asset
</button>
      </div>

      {normalizedComponents.length ? (
        <div className="space-y-2">
          {normalizedComponents.map((component) => (
            <button
              key={component.id}
              type="button"
              onClick={() => editComponent(component)}
              className={[
                "w-full text-left border rounded p-2 text-sm",
                String(component.id) === String(selectedComponentId)
                  ? "bg-blue-50 border-blue-300"
                  : "bg-white hover:bg-slate-50",
              ].join(" ")}
            >
              <div className="font-medium">
                {component.asset_role || "component.generic"}
              </div>

              <div className="text-xs text-slate-500">
  Item #{component.component_item_id || component.component_item?.id || "—"} ·{" "}
  {component.component_item?.code ||
    component.component_item?.name ||
    component.asset?.name ||
    component.asset?.code ||
    "No component item"}
</div>

<div className="text-[11px] text-slate-400">
  Asset #{component.asset_id || component.asset?.id || "—"}
</div>

              <div className="text-[11px] text-slate-400">
  Qty {component.quantity ?? 1}
</div>

              <div className="text-[11px] text-slate-400 mt-1">
                X {component.metadata_json?.position?.x ?? 0} · Y{" "}
                {component.metadata_json?.position?.y ?? 0} · Z{" "}
                {component.metadata_json?.position?.z ?? 0}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-sm text-slate-400">
          No components for this item yet.
        </div>
      )}
    </div>
  );
}

const assemblyParameters = useMemo(() => {
  const attrs = item?.attributes_json || {};

  return {
    width: 600,
    height: 720,
    depth: 560,

    cabinetInnerWidth: 600,
    cabinetInnerDepth: 500,

    sideClearance: 7.5,
    runnerHeight: 40,
    runnerLength: 300,

    drawerWidth: 600 - 2 * 7.5,
    drawerDepth: 300,
    drawerY: 0,

    ...(attrs.assembly_parameters || {}),
    ...(attrs.technical || {}),
  };
}, [item]);

function renderPreview() {
  return (
    <div className="h-full min-h-0 flex flex-col">
      <div className="shrink-0 mb-3">
        <div className="font-semibold text-sm">Item assembly preview</div>
        <div className="text-xs text-slate-500">
          Select components and position them inside this item.
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2">
  <label className="flex items-center gap-2 text-sm">
    <input
      type="checkbox"
      checked={showDrillings}
      onChange={(e) => setShowDrillings(e.target.checked)}
    />
    Show drillings
  </label>
  <label className="flex items-center gap-2 text-sm">
  <input
    type="checkbox"
    checked={showOrigins}
    onChange={(e) => setShowOrigins(e.target.checked)}
  />
  Show origins
</label>
</div>

      <div className="flex-1 min-h-0">
        <ItemAssemblyPreview3D
  itemMounting={itemMounting}
  itemMountingItems={itemMountingItems}
  components={normalizedComponents}
  selectedComponentId={selectedComponentId}
  onSelectComponent={editComponent}
  fullHeight={fullScreen}
  transformMode="translate"
  showDrillings={showDrillings}
  showOrigins={showOrigins}
  assemblyParameters={assemblyParameters}
  onTransformComponent={(componentId, transform) => {
    if (String(componentForm.id) !== String(componentId)) {
      const component = normalizedComponents.find(
        (x) => String(x.id) === String(componentId)
      );

      if (component) {
        editComponent(component);
      }
    }

    const nextMeta = {
      ...(componentForm.metadata_json || {}),
      position: transform.position,
      rotation: transform.rotation,
      parameters: componentForm.metadata_json?.parameters || {},
    };

    setComponentForm((prev) => ({
      ...prev,
      id: componentId,
      metadata_json: nextMeta,
    }));

    setMetadataText(prettyJson(nextMeta));
  }}
/>
      </div>
    </div>
  );
}

function renderInspector() {
  return (
    <div className="space-y-3">
      <div>
        <div className="font-semibold text-sm">
          {componentForm.id ? "Edit component" : "New component"}
        </div>
        <div className="text-xs text-slate-500">
          Component item, role, position, rotation and parameters.
        </div>
      </div>

      <Field label="Component Item Search">
  <input
    value={assetSearch || ""}
    onChange={(e) => setAssetSearch?.(e.target.value)}
    placeholder="Search item by name, code, SKU..."
    className="w-full px-3 py-2 border rounded"
  />
</Field>

<Field label="Component Item">
  <select
    value={componentForm.component_item_id || ""}
    onChange={(e) => {
      const selected = componentItems.find(
        (x) => String(x.id) === String(e.target.value)
      );

      setComponentForm((prev) => ({
        ...prev,
        component_item_id: e.target.value,
        asset_id: selected?.asset_id || "",
      }));
    }}
    className="w-full px-3 py-2 border rounded bg-white"
  >
    <option value="">Select component item...</option>

    {componentItems.map((componentItem) => (
      <option key={componentItem.id} value={componentItem.id}>
        {componentItem.code} · {componentItem.name} · asset {componentItem.asset_id || "—"}
      </option>
    ))}
  </select>
</Field>

      {componentForm.component_item_id ? (
  <button
    type="button"
    onClick={() => {
      window.open(
        `/interior-editor/catalog/items/${componentForm.component_item_id}/mounting`,
        "_blank"
      );
    }}
    className="w-full px-3 py-2 border rounded bg-white text-sm text-blue-700 hover:bg-blue-50"
  >
    Open Component Item Mounting
  </button>
) : null}

{componentForm.asset_id ? (
  <button
    type="button"
    onClick={() => {
      window.open(
        `/interior-editor/assets/${componentForm.asset_id}/mounting`,
        "_blank"
      );
    }}
    className="w-full px-3 py-2 border rounded bg-white text-sm text-blue-700 hover:bg-blue-50"
  >
    Open Component Asset Mounting
  </button>
) : null}

      <Field label="Component role / category">
  <select
    value={componentForm.asset_role || ""}
    onChange={(e) =>
      setComponentForm((prev) => ({
        ...prev,
        asset_role: e.target.value,
      }))
    }
    className="w-full px-3 py-2 border rounded bg-white"
  >
    <option value="">Select component role...</option>

    {componentCategories.map((category) => (
      <option
        key={category.id || category.category_key}
        value={category.category_key}
      >
        {category.label || category.category_key}
      </option>
    ))}
  </select>
</Field>

<Field label="Quantity">
  <input
    type="number"
    step="0.001"
    min="0"
    value={componentForm.quantity ?? 1}
    onChange={(e) =>
      setComponentForm((prev) => ({
        ...prev,
        quantity: Number(e.target.value || 1),
      }))
    }
    className="w-full px-3 py-2 border rounded"
  />
</Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Primary">
          <select
            value={componentForm.is_primary ? "1" : "0"}
            onChange={(e) =>
              setComponentForm((prev) => ({
                ...prev,
                is_primary: e.target.value === "1" ? 1 : 0,
              }))
            }
            className="w-full px-3 py-2 border rounded bg-white"
          >
            <option value="0">No</option>
            <option value="1">Yes</option>
          </select>
        </Field>

        <Field label="Sort order">
          <input
            type="number"
            value={componentForm.sort_order || 0}
            onChange={(e) =>
              setComponentForm((prev) => ({
                ...prev,
                sort_order: Number(e.target.value),
              }))
            }
            className="w-full px-3 py-2 border rounded"
          />
        </Field>
      </div>

      <div className="border rounded p-3 bg-slate-50 space-y-3">
        <div className="font-semibold text-sm">Position</div>

        <div className="grid grid-cols-3 gap-2">
          {["x", "y", "z"].map((axis) => (
            <Field key={axis} label={axis.toUpperCase()}>
              <input
  type="text"
  value={componentForm.metadata_json?.position?.[axis] ?? 0}
  onChange={(e) =>
    updateMeta(["position", axis], e.target.value)
  }
  className="w-full px-3 py-2 border rounded"
/>
            </Field>
          ))}
        </div>
      </div>

      <div className="border rounded p-3 bg-slate-50 space-y-3">
        <div className="font-semibold text-sm">Rotation</div>

        <div className="grid grid-cols-3 gap-2">
          {["x", "y", "z"].map((axis) => (
            <Field key={axis} label={axis.toUpperCase()}>
              <input
  type="text"
  value={componentForm.metadata_json?.rotation?.[axis] ?? 0}
  onChange={(e) =>
    updateMeta(["rotation", axis], e.target.value)
  }
  className="w-full px-3 py-2 border rounded"
/>
            </Field>
          ))}
        </div>
      </div>

      <div className="border rounded p-3 bg-slate-50 space-y-3">
  <div>
    <div className="font-semibold text-sm">Component parameters</div>
    <div className="text-xs text-slate-500">
      Controlled parameters from parameter definitions.
    </div>
  </div>

  <Field label="Add parameter">
    <select
      value=""
      onChange={(e) => addParameter(e.target.value)}
      className="w-full px-3 py-2 border rounded bg-white"
    >
      <option value="">Select parameter...</option>

      {parameterDefinitions.map((param) => (
        <option key={param.id || param.param_key} value={param.param_key}>
          {param.label || param.param_key}
        </option>
      ))}
    </select>
  </Field>

  {Object.entries(componentForm.metadata_json?.parameters || {}).length ? (
    <div className="space-y-2">
      {Object.entries(componentForm.metadata_json?.parameters || {}).map(
        ([key, value]) => {
          const def = parameterDefinitions.find(
            (p) => String(p.param_key) === String(key)
          );

          return (
            <div
              key={key}
              className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center"
            >
              <div className="px-2 py-1 border rounded bg-white text-sm">
                {def?.label || key}
                <div className="text-[10px] text-slate-400">{key}</div>
              </div>

              <input
                value={value}
                onChange={(e) => updateParameter(key, e.target.value)}
                className="px-2 py-1 border rounded text-sm"
                placeholder="value / expression"
              />

              <button
                type="button"
                onClick={() => removeParameter(key)}
                className="px-2 py-1 border rounded text-red-600 text-xs"
              >
                ×
              </button>
            </div>
          );
        }
      )}
    </div>
  ) : (
    <div className="text-xs text-slate-400">No parameters selected.</div>
  )}
</div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={componentForm.metadata_json?.enabled !== false}
          onChange={(e) => updateMeta(["enabled"], e.target.checked)}
        />
        Enabled
      </label>

      <details className="border rounded p-3 bg-white">
        <summary className="cursor-pointer text-sm font-medium">
          Advanced metadata JSON
        </summary>

        <textarea
          value={metadataText}
          onChange={(e) => setMetadataText(e.target.value)}
          rows={10}
          className="mt-3 w-full px-3 py-2 border rounded font-mono text-xs"
        />
      </details>

      <div className="flex gap-2">
        <button
          type="button"
          disabled={!item?.id || !componentForm.component_item_id}
          onClick={saveComponent}
          className="px-3 py-2 bg-green-600 text-white rounded text-sm disabled:opacity-40"
        >
          Save component
        </button>

        {componentForm.id ? (
          <button
            type="button"
            onClick={deleteComponent}
            className="px-3 py-2 border rounded text-red-600 text-sm"
          >
            Delete
          </button>
        ) : null}
      </div>
    </div>
  );
}

if (fullScreen) {
  return (
    <div className="h-full min-h-0 grid grid-cols-[320px_minmax(0,1fr)_380px] overflow-hidden bg-slate-100">
      <aside className="min-h-0 overflow-auto border-r bg-white p-3">
  {renderItemInfo()}
  {renderComponentsList()}
</aside>

      <main className="min-h-0 overflow-hidden p-3">
        {renderPreview()}
      </main>

      <aside className="min-h-0 overflow-auto border-l bg-white p-3">
        {renderInspector()}
      </aside>

      {showAddAssetModal ? (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6">
    <div className="w-full max-w-4xl max-h-[85vh] rounded bg-white shadow-xl flex flex-col overflow-hidden">
      <div className="h-12 border-b px-4 flex items-center justify-between">
        <div>
          <div className="font-semibold text-sm">Add asset to item</div>
          <div className="text-xs text-slate-500">
            Search asset by name, code or filename.
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowAddAssetModal(false)}
          className="px-3 py-1.5 border rounded text-sm"
        >
          Close
        </button>
      </div>

      <div className="p-4 border-b space-y-3">
        <Field label="Asset search">
          <input
            value={assetModalSearch}
            onChange={(e) => setAssetModalSearch(e.target.value)}
            placeholder="Search by asset name, code, filename..."
            className="w-full px-3 py-2 border rounded"
            autoFocus
          />
        </Field>

        <Field label="Component role / category">
          <select
            value={addAssetRole}
            onChange={(e) => setAddAssetRole(e.target.value)}
            className="w-full px-3 py-2 border rounded bg-white"
          >
            <option value="component.generic">Generic Component</option>

            {componentCategories.map((category) => (
              <option
                key={category.id || category.category_key}
                value={category.category_key}
              >
                {category.label || category.category_key}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-4 space-y-2">
        {assetModalResults.length ? (
          assetModalResults.map((asset) => {
            const selected = String(selectedAssetId) === String(asset.id);

            return (
              <button
                key={asset.id}
                type="button"
                onClick={() => setSelectedAssetId(asset.id)}
                className={[
                  "w-full text-left border rounded p-3 hover:bg-blue-50",
                  selected ? "border-blue-500 bg-blue-50" : "bg-white",
                ].join(" ")}
              >
                <div className="font-medium text-sm text-slate-800">
                  {asset.name || asset.code || `Asset #${asset.id}`}
                </div>

                <div className="text-xs text-slate-500">
                  #{asset.id} · {asset.code || "no code"}
                </div>

                <div className="text-[11px] text-slate-400 truncate">
                  {asset.converted_file_path ||
                    asset.source_file_path ||
                    asset.filename ||
                    ""}
                </div>
              </button>
            );
          })
        ) : (
          <div className="text-sm text-slate-400 border rounded p-4 bg-slate-50">
            No assets found.
          </div>
        )}
      </div>

      <div className="h-14 border-t px-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setShowAddAssetModal(false)}
          className="px-3 py-2 border rounded text-sm"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={addSelectedAssetToItem}
          disabled={!selectedAssetId || addingAsset}
          className="px-4 py-2 rounded bg-green-600 text-white text-sm disabled:opacity-40"
        >
          {addingAsset ? "Adding..." : "Add asset"}
        </button>
      </div>
    </div>
  </div>
) : null}
    </div>
  );
}

return (
  <div className="space-y-4">
    <div className="border rounded p-3 bg-white">
      {renderPreview()}
    </div>

    <div className="border rounded p-3 bg-white">
      {renderComponentsList()}
    </div>

    <div className="border rounded p-3 bg-white">
      {renderInspector()}
    </div>
  </div>
);
}